/**
 * OrdersPage is a page component responsible for rendering a searchable and filterable list of orders.
 * It supports:
 * - Debounced text-based search
 * - Filtering by supplier
 * - Infinite scroll for paginated data loading
 * - Modal for creating new orders
 *
 * The component uses `OrderContext` to share order state with nested components.
 *
 * @returns {JSX.Element} - The rendered orders page.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getOrders } from '../../clients/order_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { getUserSelectList } from '../../clients/user_client';
import InfiniteScroll from 'react-infinite-scroller';
import OrderModal from '../../components/Order/OrderModal';
import OrderTable from '../../components/Order/OrderTable';
import OrderFilterComponent from '../../components/Order/OrderFilterComponent';
import { Spinner } from 'react-bootstrap';
import { showToast } from '../../config_and_helpers/helpers';
import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import DropdownSelect from '../../components/Generic/DropdownSelect';
import {
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import { findUserOption } from '../../config_and_helpers/userDropdownOptions';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  normalizeListSearchParam,
  resetPaginationForPartialSearch,
  shouldBootstrapInfiniteScrollLoad,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

export const OrderContext = React.createContext({});

const normalizeParam = normalizeListSearchParam;

const ORDER_STATUS_EMPTY_LABELS = {
  fulfilled: 'fulfilled',
  unfulfilled: 'unfulfilled',
  requested: 'requested',
  received: 'received',
  sent_to_supplier: 'sent to supplier',
  arrived_unfulfilled: 'arrived, unfulfilled',
};

/** Subtitle for the orders list empty state (matches QuotesList pattern). */
function getOrdersEmptyMessage({
  searchInput,
  statusFilter,
  supplierFilter,
  userFilter,
}) {
  if (searchInput.length >= 2) {
    return `No orders match your search for "${searchInput}"`;
  }
  if (statusFilter && ORDER_STATUS_EMPTY_LABELS[statusFilter]) {
    return `No ${ORDER_STATUS_EMPTY_LABELS[statusFilter]} orders available`;
  }
  if (supplierFilter || userFilter) {
    return 'No orders match the current filters';
  }
  return 'No orders available';
}

const OrdersPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  // State to signal when an order is updated
  const [orderUpdated, setOrderUpdated] = useState(false);

  // Raw and filtered orders
  const [baseOrders, setBaseOrders] = useState([]);
  const [viewOrders, setViewOrders] = useState([]);

  // Filters and UI state
  const [supplier, setSupplier] = useState('');
  const [supplierSelectList, setSupplierSelectList] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userSelectList, setUserSelectList] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // Search-specific loading state
  const [filters, setFilters] = useState({
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const isMountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null); // Used to cancel previous requests
  const hasMoreRef = useRef(true); // Track hasMore state in ref for immediate access
  const isFetchingRef = useRef(false); // Synchronous in-flight guard for race-free pagination

  // Track component mount state to prevent updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch supplier select list on component mount
  useEffect(() => {
    getSupplierSelectList(token, setSupplierSelectList);
  }, [token]);

  useEffect(() => {
    getUserSelectList(token, setUserSelectList);
  }, [token]);

  /**
   * Fetch orders with optional search, pagination, supplier/status filters, and user filter.
   * Cleared user dropdown (no `user_id`) returns orders for all users.
   */
  const fetchOrders = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        supplierFilter = '',
        userFilter = '',
        statusFilter = '',
        sortByValue = 'created_at',
        sortDirectionValue = 'desc',
      } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        nextPage,
        supplierFilter,
        userFilter,
        statusFilter,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };

      // Prevent fetching if we know there are no more pages (unless it's a new search)
      if (!newSearch && !hasMoreRef.current && nextPage) {
        return;
      }

      // Pagination must not start a first-page fetch (nextPage null) or race an in-flight request.
      if (!newSearch) {
        if (!nextPage || isLoading || isFetchingRef.current) {
          return;
        }
      }

      // Supersede any in-flight request only when we are about to start a replacement fetch.
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
      if (newSearch) {
        isFetchingRef.current = false;
      } else if (isFetchingRef.current) {
        return;
      }

      // Additional safety check - prevent any request if hasMore is false
      if (!hasMoreRef.current && nextPage) {
        return;
      }

      // Set loading state
      isFetchingRef.current = true;
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        // Clear existing orders for new search
        setBaseOrders([]);
        setViewOrders([]);
        // Reset pagination state for new search
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      // Create a cancelable promise
      const controller = new AbortController();
      currentRequestRef.current = controller;

      getOrders(token, setBaseOrders, {
        searchInput: searchValue,
        nextPage,
        supplierFilter,
        userFilter,
        statusFilter,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
        signal: controller.signal,
      })
        .then(response => {
          if (!isMountedRef.current) return;
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            isFetchingRef.current = false;
            return;
          }

          const responseMeta = {
            success: response && response.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response && response.success) {
            // Clean the nextPage URL to prevent parameter accumulation
            let cleanNextPage = response.nextPage;
            if (cleanNextPage && !response.reachedEnd) {
              try {
                const url = new URL(cleanNextPage);
                // Keep page_num parameter and current search value, remove accumulated search parameters
                const cleanUrl = new URL(url.origin + url.pathname);
                cleanUrl.searchParams.set(
                  'page_num',
                  url.searchParams.get('page_num')
                );
                // Add current search value if it exists
                if (searchValue) {
                  cleanUrl.searchParams.set('search', searchValue);
                }
                if (supplierFilter) {
                  cleanUrl.searchParams.set('supplier_filter', supplierFilter);
                }
                if (userFilter) {
                  cleanUrl.searchParams.set('user_id', userFilter);
                }
                if (statusFilter) {
                  cleanUrl.searchParams.set('status_filter', statusFilter);
                }
                if (sortByValue) {
                  cleanUrl.searchParams.set('sort_by', sortByValue);
                }
                if (sortDirectionValue) {
                  cleanUrl.searchParams.set('sort_direction', sortDirectionValue);
                }
                cleanNextPage = cleanUrl.toString();
              } catch (error) {
                logListSearchError(
                  'orders',
                  'next-page-url-clean',
                  error,
                  { params }
                );
              }
            }

            setNextPageUrl(response.reachedEnd ? null : cleanNextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            // Set hasMore to false to stop infinite scroll from trying to load more pages
            setHasMore(false);
            hasMoreRef.current = false;
            setNextPageUrl(null);

            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'orders',
            response && response.success ? 'success' : 'failure',
            {
              trigger,
              params,
              responseMeta,
            }
          );

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          isFetchingRef.current = false;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            // Request was cancelled, do nothing
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            isFetchingRef.current = false;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('orders', trigger, error, { params });

          // Set hasMore to false to stop infinite scroll from trying to load more pages
          setHasMore(false);
          hasMoreRef.current = false;
          setNextPageUrl(null);

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          isFetchingRef.current = false;
        });
    },
    [token, isLoading]
  );

  // Cleanup effect for debounce timeout and requests
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
      isFetchingRef.current = false;
    };
  }, []);

  /**
   * Smart debounced search input handler with immediate UI feedback
   */
  const debouncedFetch = useCallback(
    (value, supplier, userFilter, status, sortByValue, sortDirectionValue) => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Immediate UI feedback: clear results and show searching state
      if (value.length >= 2) {
        setBaseOrders([]);
        setViewOrders([]);
        setIsSearching(true);
        // Reset pagination state for new search
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      // Only search if we have 2+ characters or empty string
      if (isRunnableListSearchQuery(value)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchOrders(
            {
              searchValue: value,
              nextPage: null,
              supplierFilter: supplier,
              userFilter,
              statusFilter: status,
              sortByValue,
              sortDirectionValue,
            },
            true
          ); // newSearch = true for search operations
        }, 300); // Reduced from 500ms to 300ms for better responsiveness
      } else if (isPartialListSearchQuery(value)) {
        // Partial query: clear results and kill stale pagination so InfiniteScroll
        // cannot loadMore against the previous next-page URL.
        setBaseOrders([]);
        setViewOrders([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchOrders]
  );

  /**
   * Handles updates to the search input.
   */
  const handleSearchInput = value => {
    setSearchInput(value);
    debouncedFetch(
      value,
      supplier,
      selectedUser,
      filters.status,
      sortBy,
      sortDirection
    );
  };

  /**
   * Handles selection of a supplier filter.
   */
  const handleSupplierChange = value => {
    const supplierValue = normalizeParam(value);
    setSupplier(supplierValue);
    fetchOrders(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplierValue,
        userFilter: selectedUser,
        statusFilter: filters.status,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    ); // newSearch = true for filter changes
  };

  const handleUserChange = value => {
    const userValue = normalizeParam(value);
    setSelectedUser(userValue);
    fetchOrders(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplier,
        userFilter: userValue,
        statusFilter: filters.status,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    );
  };

  /**
   * Clears the current search value.
   */
  const resetSearchValue = () => setSearchInput('');

  // Removed handleApplyFilters since filters are now applied automatically

  const handleClearFilters = () => {
    fetchOrders(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplier,
        userFilter: selectedUser,
        statusFilter: '',
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    ); // newSearch = true for filter changes
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSortByChange = (value, direction = 'asc') => {
    setSortBy(value);
    setSortDirection(direction);
    fetchOrders(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplier,
        userFilter: selectedUser,
        statusFilter: filters.status,
        sortByValue: value,
        sortDirectionValue: direction,
      },
      true
    );
  };

  return (
    <GlassWorkspaceShell theme="yellow" wide>
      <OrderContext.Provider
        value={{
          orderUpdated,
          setOrderUpdated,
          setOrders: viewOrders.length ? setViewOrders : setBaseOrders,
          orders: viewOrders.length ? viewOrders : baseOrders,
        }}
      >
        <div className="glass-toolbar">
          {/* Reusable search + filter bar */}
          <GenericSearchFilter
            entityType="suppliers"
            placeHolderLabel="Supplier"
            searchInput={searchInput}
            onSearchInput={handleSearchInput}
            entity={supplier}
            onEntityChange={handleSupplierChange}
            onClear={resetSearchValue}
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            supplierSelectList={supplierSelectList}
            customActionButton={
              <div style={{ width: '100%', minWidth: '200px', maxWidth: '260px' }}>
                <DropdownSelect
                  optionsList={userSelectList}
                  label="User"
                  selectedValue={findUserOption(userSelectList, selectedUser)}
                  setSelectedValue={option =>
                    handleUserChange(option?.value || '')
                  }
                  disabledOptions={[]}
                  clearable={true}
                />
              </div>
            }
            ModalComponent={
              isViewer ? null : (
              <OrderModal
                onSuccessfulSubmit={() =>
                  fetchOrders(
                    {
                      searchValue: searchInput,
                      nextPage: null,
                      supplierFilter: supplier,
                      userFilter: selectedUser,
                      statusFilter: filters.status,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  )
                } // newSearch = true for refresh after modal submit
                clearSearchValue={resetSearchValue}
              />
              )
            }
          />
          {showFilters && (
            <OrderFilterComponent
              filters={filters}
              setFilters={setFilters}
              onApplyFilters={newStatus => {
                fetchOrders(
                  {
                    searchValue: searchInput,
                    nextPage: null,
                    supplierFilter: supplier,
                    userFilter: selectedUser,
                    statusFilter: newStatus,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  true
                ); // newSearch = true for filter changes
              }}
              onClearFilters={handleClearFilters}
            />
          )}
        </div>

      {/* Loading state with search feedback */}
      {(isLoading && !baseOrders.length && !viewOrders.length) ||
      isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">Searching orders...</div>
          )}
        </div>
      ) : (
            <InfiniteScroll
              key={`${searchInput}-${supplier}-${selectedUser}-${filters.status}-${sortBy}-${sortDirection}-${hasMore}`} // Force reset when state changes
              pageStart={0}
              initialLoad={hasMoreRef.current} // Only load initially if we have more pages
              loadMore={() => {
                if (
                  isLoading ||
                  isFetchingRef.current ||
                  !hasMoreRef.current ||
                  isPartialListSearchQuery(searchInput)
                ) {
                  return;
                }

                const itemCount = (viewOrders.length || baseOrders.length) || 0;

                // InfiniteScroll initialLoad: fetch page 1 when the list is still empty.
                if (
                  shouldBootstrapInfiniteScrollLoad({
                    nextPageUrl,
                    itemCount,
                  })
                ) {
                  fetchOrders(
                    {
                      searchValue: searchInput,
                      nextPage: null,
                      supplierFilter: supplier,
                      userFilter: selectedUser,
                      statusFilter: filters.status,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  );
                  return;
                }

                // Pagination requires a real next-page URL.
                if (!nextPageUrl) {
                  return;
                }

                // Prevent using stale nextPage URLs when search/filter/sort has changed
                const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                  search: searchInput,
                  supplier_filter: supplier,
                  user_id: selectedUser,
                  status_filter: filters.status,
                  sort_by: sortBy,
                  sort_direction: sortDirection,
                });
                if (mismatch === null) {
                  logListSearchError(
                    'orders',
                    'next-page-url-stale-check',
                    new Error('Failed to parse nextPageUrl'),
                    {
                      nextPageUrl,
                      searchInput,
                      supplier,
                      selectedUser,
                      status: filters.status,
                      sortBy,
                      sortDirection,
                    }
                  );
                  setNextPageUrl(null);
                  setHasMore(false);
                  hasMoreRef.current = false;
                  return;
                }
                if (mismatch.mismatched) {
                  logListSearchEvent('orders', 'next-page-url-stale-mismatch', {
                    nextPageUrl,
                    expected: mismatch.expected,
                    actual: mismatch.actual,
                  });
                  setNextPageUrl(null);
                  setHasMore(false);
                  hasMoreRef.current = false;
                  return;
                }

                fetchOrders(
                  {
                    searchValue: searchInput,
                    nextPage: nextPageUrl,
                    supplierFilter: supplier,
                    userFilter: selectedUser,
                    statusFilter: filters.status,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  false
                ); // newSearch = false for pagination
              }}
              hasMore={hasMore && !isLoading}
              loader={
                <div key="orders-loader" className="text-center py-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <div className="mt-1">
                    <small className="text-muted">Loading more orders...</small>
                  </div>
                </div>
              }
              useWindow={true}
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
            >
              {!(viewOrders.length || baseOrders.length) ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <span
                      role="img"
                      aria-label="no results"
                      style={{ fontSize: '3rem' }}
                    >
                      📦
                    </span>
                  </div>
                  <h5 className="text-muted mb-2">No orders found</h5>
                  <p className="text-muted mb-3">
                    {getOrdersEmptyMessage({
                      searchInput,
                      statusFilter: filters.status,
                      supplierFilter: supplier,
                      userFilter: selectedUser,
                    })}
                  </p>
                  {searchInput.length >= 2 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setSearchInput('');
                        fetchOrders(
                          {
                            searchValue: '',
                            nextPage: null,
                            supplierFilter: supplier,
                            userFilter: selectedUser,
                            statusFilter: filters.status,
                            sortByValue: sortBy,
                            sortDirectionValue: sortDirection,
                          },
                          true
                        );
                      }}
                    >
                      <span role="img" aria-label="clear search">
                        🗑️
                      </span>{' '}
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div key="order-table" className="glass-table-wrap">
                  <OrderTable
                    orderList={viewOrders.length ? viewOrders : baseOrders}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortByChange}
                    handleDelete={() =>
                      fetchOrders(
                        {
                          searchValue: searchInput,
                          nextPage: null,
                          supplierFilter: supplier,
                          userFilter: selectedUser,
                          statusFilter: filters.status,
                          sortByValue: sortBy,
                          sortDirectionValue: sortDirection,
                        },
                        true
                      )
                    } // newSearch = true for refresh after delete
                    clearSearchValue={resetSearchValue}
                  />
                </div>
              )}
            </InfiniteScroll>
      )}
      </OrderContext.Provider>
    </GlassWorkspaceShell>
  );
};

export default OrdersPage;
